import { HttpClient } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { Subscription } from 'rxjs';

import { Constant } from 'src/app/constant/constant';

@Component({
  selector: 'calsoft-common-images',
  templateUrl: './common-images.component.html',
  styleUrls: ['./common-images.component.scss']
})
export class CommonImagesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() src: string;
  @Input() alt;
  @Input() onerror;
  @Input() width = '50px';
  @Input() height = '50px';
  cid: string;
  display: boolean = true;
  safeUrl: SafeUrl | string = 'assets/images/logo/no-image.png';
  fileType: string = 'image';
  private currentObjectUrl: string | null = null;
  private subscription: Subscription;


  constructor(private http: HttpClient, private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    let values = localStorage.getItem(Constant.enterPriseKey);
    if (this.src == undefined || this.src == null) {
      this.display = false;
    } else if (values != null) {
      this.cid = "&cid=" + values;
    }
    this.loadImage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['src'] && !changes['src'].firstChange) {
      this.loadImage();
    }
  }

  getFileNameFromUrl(src: string): string {
    if (!src) return '';
    let filename = '';

    // Check if there is a query string
    if (src.includes('?')) {
      const queryString = src.split('?')[1];
      const params = queryString.split('&');
      for (const param of params) {
        const pair = param.split('=');
        const key = pair[0].toLowerCase();
        if (key === 'filename' || key === 'file' || key === 'name') {
          filename = decodeURIComponent(pair[1] || '');
          break;
        }
      }
    }

    if (!filename) {
      const cleanSrc = src.split('?')[0];
      const parts = cleanSrc.split('/');
      filename = parts[parts.length - 1];
    }

    // Extract file component if name includes path delimiters
    if (filename.includes('/')) {
      const parts = filename.split('/');
      filename = parts[parts.length - 1];
    } else if (filename.includes('\\')) {
      const parts = filename.split('\\');
      filename = parts[parts.length - 1];
    }

    return filename;
  }

  detectFileType(src: string): void {
    if (!src) {
      this.fileType = 'image';
      return;
    }
    
    const filename = this.getFileNameFromUrl(src);
    if (!filename || !filename.includes('.')) {
      this.fileType = 'image';
      return;
    }
    const extParts = filename.split('.');
    const ext = extParts[extParts.length - 1].toLowerCase();

    const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp'];
    const pdfExtensions = ['pdf'];
    const excelExtensions = ['xls', 'xlsx', 'csv', 'ods'];
    const docExtensions = ['doc', 'docx', 'odt'];
    const videoExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'mkv'];

    if (imageExtensions.includes(ext)) {
      this.fileType = 'image';
    } else if (pdfExtensions.includes(ext)) {
      this.fileType = 'pdf';
    } else if (excelExtensions.includes(ext)) {
      this.fileType = 'excel';
    } else if (docExtensions.includes(ext)) {
      this.fileType = 'doc';
    } else if (videoExtensions.includes(ext)) {
      this.fileType = 'video';
    } else {
      this.fileType = 'file';
    }
  }

  getFileName(): string {
    return this.getFileNameFromUrl(this.src) || 'attachment';
  }

  getIconName(): string {
    switch (this.fileType) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'excel':
        return 'table_chart';
      case 'doc':
        return 'description';
      case 'video':
        return 'movie';
      default:
        return 'insert_drive_file';
    }
  }

  downloadFile(): void {
    if (!this.src || this.src.includes('undefined') || this.src.includes('null') || this.src.endsWith('/')) {
      return;
    }
    const filename = this.getFileName();
    if (this.currentObjectUrl) {
      const link = document.createElement('a');
      link.href = this.currentObjectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const fullUrl = this.src + (this.cid || '');
      this.http.get(fullUrl, { responseType: 'blob' }).subscribe(
        (blob) => {
          const objUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objUrl);
        },
        (error) => {
          window.open(fullUrl, '_blank');
        }
      );
    }
  }

  loadImage(): void {
    if (!this.src || this.src.includes('undefined') || this.src.includes('null') || this.src.endsWith('/')) {
      this.safeUrl = 'assets/images/logo/no-image.png';
      this.fileType = 'image';
      return;
    }

    this.detectFileType(this.src);

    const fullUrl = this.src + (this.cid || '');

    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
      this.currentObjectUrl = null;
    }

    this.subscription = this.http.get(fullUrl, { responseType: 'blob' }).subscribe(
      (blob) => {
        this.currentObjectUrl = URL.createObjectURL(blob);
        this.safeUrl = this.sanitizer.bypassSecurityTrustUrl(this.currentObjectUrl);
      },

      (error) => {
        this.safeUrl = 'assets/images/logo/no-image.png';
      }
    );
  }

  onImageError(event: Event) {
    this.safeUrl = 'assets/images/logo/no-image.png';
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
    }
  }

}

